'use client';

import * as React from 'react';

import {
  type ColumnFiltersState,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type TableState,
  type Updater,
  useReactTable,
} from '@tanstack/react-table';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  type Parser,
  useQueryState,
  type UseQueryStateOptions,
  useQueryStates,
} from 'nuqs';

import { useColumnOrder } from '@/hooks/use-column-order';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { getSortingStateParser } from '@/lib/parsers';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';

const COLUMN_SIZING_STORAGE_PREFIX = 'table-column-sizing';

const PAGE_KEY = 'page';
const PER_PAGE_KEY = 'perPage';
const SORT_KEY = 'sort';
const ARRAY_SEPARATOR = ',';
const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;

interface UseDataTableProps<TData>
  extends Omit<
    TableOptions<TData>,
    | 'state'
    | 'pageCount'
    | 'getCoreRowModel'
    | 'manualFiltering'
    | 'manualPagination'
    | 'manualSorting'
  > {
  pageCount?: number;
  initialState?: Omit<Partial<TableState>, 'sorting'> & {
    sorting?: ExtendedColumnSort<TData>[];
  };
  history?: 'push' | 'replace';
  debounceMs?: number;
  throttleMs?: number;
  clearOnDefault?: boolean;
  enableAdvancedFilter?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  startTransition?: React.TransitionStartFunction;
  /** Unique identifier for localStorage persistence of column widths */
  tableId?: string;
}

export function useDataTable<TData>(props: UseDataTableProps<TData>) {
  const {
    columns,
    pageCount = -1,
    initialState,
    history = 'replace',
    debounceMs = DEBOUNCE_MS,
    throttleMs = THROTTLE_MS,
    clearOnDefault = false,
    enableAdvancedFilter = false,
    scroll = false,
    shallow = true,
    startTransition,
    enableRowSelection = true,
    tableId,
    ...tableProps
  } = props;

  const queryStateOptions = React.useMemo<Omit<UseQueryStateOptions<string>, 'parse'>>(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    }),
    [history, scroll, shallow, throttleMs, debounceMs, clearOnDefault, startTransition]
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
    initialState?.rowSelection ?? {}
  );

  // Column visibility state with localStorage persistence (shared hook — same storage key
  // as tables that manage their own TanStack instance, e.g. the approvals table).
  const {
    columnVisibility,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    saveColumnVisibility,
  } = useColumnVisibility(tableId, { initialVisibility: initialState?.columnVisibility });

  // Column order state with localStorage persistence (shared hook — same storage key
  // as tables that manage their own TanStack instance).
  const {
    columnOrder,
    onColumnOrderChange: handleColumnOrderChange,
    saveColumnOrder,
  } = useColumnOrder(tableId, { initialOrder: initialState?.columnOrder });

  // Column sizing state with localStorage persistence
  const columnSizingStorageKey = tableId ? `${COLUMN_SIZING_STORAGE_PREFIX}-${tableId}` : null;

  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(() => {
    if (!columnSizingStorageKey) return initialState?.columnSizing ?? {};
    const saved = getFromLocalStorage<ColumnSizingState>(columnSizingStorageKey);
    return saved ?? initialState?.columnSizing ?? {};
  });

  const columnSizingRef = React.useRef(columnSizing);
  columnSizingRef.current = columnSizing;

  // Throttle column sizing updates using requestAnimationFrame to prevent excessive re-renders
  // during live column resize ('onChange' mode). Without throttling, every mouse pixel movement
  // triggers a React state update → full table re-render → Radix PopperAnchor infinite loop.
  const pendingColumnSizingRef = React.useRef<ColumnSizingState | null>(null);
  const rafIdRef = React.useRef<number | null>(null);

  const throttledSetColumnSizing = React.useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      pendingColumnSizingRef.current =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizingRef.current)
          : updaterOrValue;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          if (pendingColumnSizingRef.current !== null) {
            setColumnSizing(pendingColumnSizingRef.current);
            columnSizingRef.current = pendingColumnSizingRef.current;
            pendingColumnSizingRef.current = null;
          }
          rafIdRef.current = null;
        });
      }
    },
    []
  );

  // Column sizing INFO state — managed via ref to avoid re-renders on every mouse move.
  // TanStack updates columnSizingInfo on every pixel of drag movement (deltaOffset, deltaPercentage).
  // If this triggers React state updates, the entire table re-renders per pixel, causing
  // Radix PopperAnchor's dependency-less useEffect to hit "Maximum update depth exceeded".
  //
  // Strategy: keep the real value in a ref (so TanStack's internal closure logic works),
  // but only update React state on resize start/end (isResizingColumn changes).
  const defaultSizingInfo: ColumnSizingInfoState = {
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false,
    columnSizingStart: [],
  };

  const [columnSizingInfoState, setColumnSizingInfoState] =
    React.useState<ColumnSizingInfoState>(defaultSizingInfo);
  const columnSizingInfoRef = React.useRef<ColumnSizingInfoState>(defaultSizingInfo);

  const handleColumnSizingInfoChange = React.useCallback(
    (updaterOrValue: Updater<ColumnSizingInfoState>) => {
      const newInfo =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizingInfoRef.current)
          : updaterOrValue;

      const prevIsResizing = columnSizingInfoRef.current.isResizingColumn;
      columnSizingInfoRef.current = newInfo;

      // Only trigger React re-render when resize starts or ends
      if (prevIsResizing !== newInfo.isResizingColumn) {
        setColumnSizingInfoState(newInfo);
      }
    },
    []
  );

  // Cleanup RAF on unmount
  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const saveColumnSizing = React.useCallback(() => {
    if (columnSizingStorageKey) {
      setToLocalStorage(columnSizingStorageKey, columnSizingRef.current);
    }
  }, [columnSizingStorageKey]);

  const [page, setPage] = useQueryState(
    PAGE_KEY,
    parseAsInteger.withOptions(queryStateOptions).withDefault(1)
  );
  const [perPage, setPerPage] = useQueryState(
    PER_PAGE_KEY,
    parseAsInteger
      .withOptions(queryStateOptions)
      .withDefault(initialState?.pagination?.pageSize ?? 10)
  );

  const pagination: PaginationState = React.useMemo(() => {
    return {
      pageIndex: page - 1, // zero-based index -> one-based index
      pageSize: perPage,
    };
  }, [page, perPage]);

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      if (typeof updaterOrValue === 'function') {
        const newPagination = updaterOrValue(pagination);
        void setPage(newPagination.pageIndex + 1);
        void setPerPage(newPagination.pageSize);
      } else {
        void setPage(updaterOrValue.pageIndex + 1);
        void setPerPage(updaterOrValue.pageSize);
      }
    },
    [pagination, setPage, setPerPage]
  );

  const columnIds = React.useMemo(() => {
    return new Set(columns.map(column => column.id).filter(Boolean) as string[]);
  }, [columns]);

  const [sorting, setSorting] = useQueryState(
    SORT_KEY,
    getSortingStateParser<TData>(columnIds)
      .withOptions(queryStateOptions)
      .withDefault(initialState?.sorting ?? [])
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(sorting);
        setSorting(newSorting as ExtendedColumnSort<TData>[]);
      } else {
        setSorting(updaterOrValue as ExtendedColumnSort<TData>[]);
      }
    },
    [sorting, setSorting]
  );

  const filterableColumns = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return columns.filter(column => column.enableColumnFilter);
  }, [columns, enableAdvancedFilter]);

  const filterParsers = React.useMemo(() => {
    if (enableAdvancedFilter) return {};

    return filterableColumns.reduce<Record<string, Parser<string> | Parser<string[]>>>(
      (acc, column) => {
        if (column.meta?.options) {
          acc[column.id ?? ''] = parseAsArrayOf(parseAsString, ARRAY_SEPARATOR).withOptions(
            queryStateOptions
          );
        } else {
          acc[column.id ?? ''] = parseAsString.withOptions(queryStateOptions);
        }
        return acc;
      },
      {}
    );
  }, [filterableColumns, queryStateOptions, enableAdvancedFilter]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers);

  const debouncedSetFilterValues = useDebouncedCallback((values: typeof filterValues) => {
    void setPage(1);
    void setFilterValues(values);
  }, debounceMs);

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return Object.entries(filterValues).reduce<ColumnFiltersState>((filters, [key, value]) => {
      if (value !== null) {
        const processedValue = Array.isArray(value)
          ? value
          : typeof value === 'string' && /[^a-zA-Z0-9]/.test(value)
            ? value.split(/[^a-zA-Z0-9]+/).filter(Boolean)
            : [value];

        filters.push({
          id: key,
          value: processedValue,
        });
      }
      return filters;
    }, []);
  }, [filterValues, enableAdvancedFilter]);

  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      setColumnFilters(prev => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;

        const filterUpdates = next.reduce<Record<string, string | string[] | null>>(
          (acc, filter) => {
            if (filterableColumns.find(column => column.id === filter.id)) {
              acc[filter.id] = filter.value as string | string[];
            }
            return acc;
          },
          {}
        );

        for (const prevFilter of prev) {
          if (!next.some(filter => filter.id === prevFilter.id)) {
            filterUpdates[prevFilter.id] = null;
          }
        }

        debouncedSetFilterValues(filterUpdates);
        return next;
      });
    },
    [debouncedSetFilterValues, filterableColumns, enableAdvancedFilter]
  );

  const table = useReactTable({
    ...tableProps,
    columns,
    initialState,
    ...(typeof pageCount === 'number' ? { pageCount } : {}),
    state: {
      ...(typeof pageCount === 'number' ? { pagination } : {}),
      sorting,
      columnVisibility,
      columnOrder,
      rowSelection,
      columnFilters,
      columnSizing,
      columnSizingInfo: columnSizingInfoState,
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false,
      minSize: 100,
      maxSize: 600,
    },
    enableRowSelection,
    enableColumnResizing: !!tableId,
    columnResizeMode: 'onChange',
    onRowSelectionChange: setRowSelection,
    ...(typeof pageCount === 'number' ? { onPaginationChange } : {}),
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnSizingChange: throttledSetColumnSizing,
    onColumnSizingInfoChange: handleColumnSizingInfoChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(typeof pageCount === 'number' ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    ...(typeof pageCount === 'number'
      ? {
          manualPagination: true,
          manualSorting: true,
          manualFiltering: true,
        }
      : {}),
  });

  return {
    table,
    shallow,
    debounceMs,
    throttleMs,
    saveColumnSizing,
    saveColumnVisibility,
    saveColumnOrder,
  };
}
